import { Box, Paper, Stack, TextField, Typography } from '@mui/material';

import AddressWidget from '../../../components/AddressWidget';
import {
  CITYSPRINT_DETAILS_LABELS,
  CITYSPRINT_DETAILS_SECTIONS,
  CITYSPRINT_INSTRUCTIONS_MAX_LENGTH,
} from '../constants';
import { useCitysprintAddressForWidget } from '../hooks/useCitysprintAddressForWidget';

function ParcelInstructionsTextField({
  id,
  fieldKey,
  label,
  value,
  setField,
  fieldErrors = {},
  fieldTouched = {},
  onFieldBlur,
}) {
  const showError = fieldTouched[fieldKey] && fieldErrors[fieldKey];
  return (
    <Box sx={{ width: '100%', p: 1, pb: 2 }}>
      <TextField
        fullWidth
        multiline
        minRows={3}
        variant='outlined'
        size='medium'
        id={id}
        name={fieldKey}
        label={label}
        value={value}
        onChange={e => setField(fieldKey, e.target.value)}
        onBlur={() => onFieldBlur?.(fieldKey)}
        error={!!showError}
        helperText={showError ? fieldErrors[fieldKey] : undefined}
        slotProps={{
          htmlInput: { maxLength: CITYSPRINT_INSTRUCTIONS_MAX_LENGTH },
        }}
      />
    </Box>
  );
}

function ParcelContactTextField({
  id,
  fieldKey,
  label,
  type = 'text',
  value,
  setField,
  autoComplete,
  maxLength,
  required,
  fieldErrors,
  fieldTouched,
  onFieldBlur,
}) {
  const showError = fieldTouched[fieldKey] && fieldErrors[fieldKey];
  return (
    <TextField
      fullWidth
      variant='outlined'
      size='medium'
      required={required}
      id={id}
      name={fieldKey}
      label={label}
      type={type}
      value={value}
      onChange={e => setField(fieldKey, e.target.value)}
      onBlur={() => onFieldBlur?.(fieldKey)}
      autoComplete={autoComplete}
      error={!!showError}
      helperText={showError ? fieldErrors[fieldKey] : undefined}
      slotProps={{ htmlInput: { maxLength } }}
    />
  );
}

function ParcelContactDetailsRow({
  idPrefix,
  fieldKeys,
  labels,
  requiredFields = {},
  form,
  setField,
  autoCompleteName = 'name',
  autoCompleteEmail = 'email',
  autoCompleteTel = 'tel',
  fieldErrors = {},
  fieldTouched = {},
  onFieldBlur,
}) {
  const { fullName, email, phone } = fieldKeys;
  const fields = [
    {
      fieldKey: fullName,
      idSuffix: 'full-name',
      label: labels.fullName,
      type: 'text',
      maxLength: 35,
      autoComplete: autoCompleteName,
      required: !!requiredFields.fullName,
    },
    {
      fieldKey: email,
      idSuffix: 'email',
      label: labels.email,
      type: 'email',
      maxLength: 100,
      autoComplete: autoCompleteEmail,
      required: !!requiredFields.email,
    },
    {
      fieldKey: phone,
      idSuffix: 'phone',
      label: labels.phone,
      type: 'tel',
      maxLength: 15,
      autoComplete: autoCompleteTel,
      required: !!requiredFields.phone,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        my: 1,
      }}
    >
      {fields.map(
        ({
          fieldKey,
          idSuffix,
          label,
          type,
          maxLength,
          autoComplete,
          required,
        }) => (
          <Box
            key={fieldKey}
            sx={{
              width: { xs: '100%', md: '50%' },
              p: 1,
              pb: 2,
            }}
          >
            <ParcelContactTextField
              id={`${idPrefix}-${idSuffix}`}
              fieldKey={fieldKey}
              label={label}
              type={type}
              value={form[fieldKey] ?? ''}
              setField={setField}
              autoComplete={autoComplete}
              maxLength={maxLength}
              required={required}
              fieldErrors={fieldErrors}
              fieldTouched={fieldTouched}
              onFieldBlur={onFieldBlur}
            />
          </Box>
        )
      )}
    </Box>
  );
}

export function CitysprintCompleteBookingForm({
  booking,
  form,
  setField,
  sectionHeadingSx,
  onEditAddresses,
  receiverContactErrors = {},
  receiverContactTouched = {
    deliveryFullName: false,
    deliveryEmail: false,
    deliveryPhone: false,
    deliveryInstructions: false,
  },
  onReceiverContactBlur,
  yourContactErrors = {},
  yourContactTouched = {
    collectionFullName: false,
    collectionEmail: false,
    collectionPhone: false,
    collectionInstructions: false,
  },
  onYourContactBlur,
}) {
  const collectionForWidget = useCitysprintAddressForWidget(
    'collection',
    booking,
    form
  );
  const deliveryForWidget = useCitysprintAddressForWidget(
    'delivery',
    booking,
    form
  );

  return (
    <form
      id='citysprintCompleteBookingForm'
      onSubmit={e => {
        e.preventDefault();
      }}
    >
      <Stack spacing={0}>
        <Paper
          sx={{ p: 2, mb: 2 }}
          role='region'
          aria-labelledby='cb-your-details-title'
        >
          <Typography
            variant='h3'
            component='h2'
            id='cb-your-details-title'
            sx={sectionHeadingSx}
          >
            {CITYSPRINT_DETAILS_SECTIONS.YOUR_DETAILS}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <ParcelContactDetailsRow
              idPrefix='cb-your'
              fieldKeys={{
                fullName: 'collectionFullName',
                email: 'collectionEmail',
                phone: 'collectionPhone',
              }}
              labels={{
                fullName: CITYSPRINT_DETAILS_LABELS.YOUR_FULL_NAME,
                email: CITYSPRINT_DETAILS_LABELS.YOUR_EMAIL,
                phone: CITYSPRINT_DETAILS_LABELS.YOUR_PHONE,
              }}
              requiredFields={{ fullName: true, email: true, phone: true }}
              form={form}
              setField={setField}
              autoCompleteName='name'
              autoCompleteEmail='email'
              autoCompleteTel='tel'
              fieldErrors={yourContactErrors}
              fieldTouched={yourContactTouched}
              onFieldBlur={onYourContactBlur}
            />
            <ParcelInstructionsTextField
              id='cb-your-collection-instructions'
              fieldKey='collectionInstructions'
              label={CITYSPRINT_DETAILS_LABELS.COLLECTION_INSTRUCTIONS}
              value={form.collectionInstructions ?? ''}
              setField={setField}
              fieldErrors={yourContactErrors}
              fieldTouched={yourContactTouched}
              onFieldBlur={onYourContactBlur}
            />
            <AddressWidget
              address={collectionForWidget}
              onEditClick={onEditAddresses}
            />
          </Box>
        </Paper>

        <Paper
          sx={{ p: 2, mb: 2 }}
          role='region'
          aria-labelledby='cb-receiver-details-title'
        >
          <Typography
            variant='h3'
            component='h2'
            id='cb-receiver-details-title'
            sx={sectionHeadingSx}
          >
            {CITYSPRINT_DETAILS_SECTIONS.RECEIVER_DETAILS}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <ParcelContactDetailsRow
              idPrefix='cb-receiver'
              fieldKeys={{
                fullName: 'deliveryFullName',
                email: 'deliveryEmail',
                phone: 'deliveryPhone',
              }}
              labels={{
                fullName: CITYSPRINT_DETAILS_LABELS.RECEIVER_FULL_NAME,
                email: CITYSPRINT_DETAILS_LABELS.RECEIVER_EMAIL,
                phone: CITYSPRINT_DETAILS_LABELS.RECEIVER_PHONE,
              }}
              requiredFields={{ fullName: true }}
              form={form}
              setField={setField}
              autoCompleteName='name'
              autoCompleteEmail='shipping email'
              autoCompleteTel='tel'
              fieldErrors={receiverContactErrors}
              fieldTouched={receiverContactTouched}
              onFieldBlur={onReceiverContactBlur}
            />
            <ParcelInstructionsTextField
              id='cb-receiver-delivery-instructions'
              fieldKey='deliveryInstructions'
              label={CITYSPRINT_DETAILS_LABELS.DELIVERY_INSTRUCTIONS}
              value={form.deliveryInstructions ?? ''}
              setField={setField}
              fieldErrors={receiverContactErrors}
              fieldTouched={receiverContactTouched}
              onFieldBlur={onReceiverContactBlur}
            />
            <AddressWidget
              address={deliveryForWidget}
              onEditClick={onEditAddresses}
            />
          </Box>
        </Paper>
      </Stack>
    </form>
  );
}
